/**
 * CatEntity - Chasing enemy that pathfinds toward the player
 *
 * Cats move every 500ms and use simple pathfinding to chase the player.
 * They prioritize movement along the axis with greater distance.
 */

import { BaseTile, MoveContext, MoveResult, TextureDescriptor } from "./BaseTile";
import { TILE, isEmpty } from "../constants/gameConstants";
import { Coordinates, TileAndEntity, TileIndex } from "../types";
import { Direction } from "../utils/Direction";
import { GAME_CONFIG } from "../config";

export class CatEntity extends BaseTile {
  // Movement timing
  timeSinceLastMove: number = 0;
  moveInterval: number = GAME_CONFIG.catSpeed;

  // Cat index for alternating behavior when distance is equal
  private index: number;

  constructor({
    row,
    col,
    index = 0,
    id,
  }: {
    row: number;
    col: number;
    index?: number;
    id?: string;
  }) {
    super(id ?? `cat-${col}-${row}-${Date.now()}`, col, row);
    this.index = index;
  }

  tileType(): TileIndex {
    return TILE.CAT;
  }

  textureDescriptor(): TextureDescriptor {
    return { type: "png", path: "tiles/cat.png" };
  }

  isEntity(): boolean {
    return true; // Cat is a movable entity
  }

  move(context: MoveContext): MoveResult {
    // Accumulate time
    this.timeSinceLastMove += context.deltaMs;

    // Not ready to move yet?
    if (this.timeSinceLastMove < this.moveInterval) {
      return { type: "wait" };
    }

    // Reset timer
    this.timeSinceLastMove = 0;

    // Get player position from context
    const playerPos = context.getPlayer();
    if (!playerPos) {
      // Player doesn't exist (shouldn't happen), don't move
      return { type: "wait" };
    }

    // Calculate distance to player
    const dx = playerPos.col - this.col;
    const dy = playerPos.row - this.row;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Determine movement priority based on distance
    let primaryDirection: Direction | null = null;
    let secondaryDirection: Direction | null = null;

    if (absDx > absDy) {
      // Prioritize horizontal movement
      primaryDirection = dx > 0 ? new Direction("right") : new Direction("left");
      if (dy !== 0) {
        secondaryDirection = dy > 0 ? new Direction("down") : new Direction("up");
      }
    } else if (absDy > absDx) {
      // Prioritize vertical movement
      primaryDirection = dy > 0 ? new Direction("down") : new Direction("up");
      if (dx !== 0) {
        secondaryDirection = dx > 0 ? new Direction("right") : new Direction("left");
      }
    } else if (absDx === absDy && absDx > 0) {
      // Equal distance, alternate based on cat index
      if (this.index % 2 === 0) {
        // Even index: try horizontal first
        primaryDirection = dx > 0 ? new Direction("right") : new Direction("left");
        secondaryDirection = dy > 0 ? new Direction("down") : new Direction("up");
      } else {
        // Odd index: try vertical first
        primaryDirection = dy > 0 ? new Direction("down") : new Direction("up");
        secondaryDirection = dx > 0 ? new Direction("right") : new Direction("left");
      }
    }

    const moveDirection = primaryDirection || secondaryDirection;
    if (!moveDirection) {
      // Already at player position, don't move
      return { type: "wait" };
    }
    const target = context.getAtDirection(this.coordinates(), moveDirection);

    if (target) {
      // Check for bomb collision (both explode)
      if (target.tile === TILE.BOMB) {
        return { type: "die", collisionWith: "tile", who: "both", direction: moveDirection };
      } else if (target.entity === TILE.PLAYER_START) {
        return { type: "die", collisionWith: "entity", who: "both", direction: moveDirection };
      } else if (target.tile === TILE.BUTTON) {
        return { type: "pressButton", direction: moveDirection };
      }


      if (this.canMoveTo(target)) {
        return { type: "move", direction: moveDirection };
      }
    }
    return { type: "wait" };
  }

  /**
   * Check if cat can move to the target position
   */
  private canMoveTo(target: { tile: TileIndex | null; entity: TileIndex | null }): boolean {
    const { tile, entity } = target;

    // Out of bounds
    if (tile === null) {
      return false;
    }

    const actAsWalls: TileIndex[] = [
      TILE.WALL,
      TILE.DOOR_GREEN,
      TILE.DOOR_ORANGE,
      TILE.DOOR_RED,
      TILE.DOOR_BLUE,
      TILE.BUTTON_DOOR,
      TILE.EXIT,
      TILE.SAND,
      TILE.WATER,
      TILE.ARROW_UP,
      TILE.ARROW_DOWN,
      TILE.ARROW_LEFT,
      TILE.ARROW_RIGHT,
      TILE.STAR,
      TILE.SUCTION_BOOTS,
      TILE.FLIPPERS,
      TILE.KEY_BLUE,
      TILE.KEY_RED,
      TILE.KEY_GREEN,
      TILE.KEY_ORANGE,
    ];

    if (actAsWalls.includes(tile)) {
      return false;
    }

    if (entity === TILE.PLAYER_START) {
      return true;
    }

    // Check entity layer - any entity blocks
    if (!isEmpty(entity)) {
      return false;
    }

    // Otherwise passable
    return true;
  }

  clone(): CatEntity {
    const copy = new CatEntity({
      col: this.col,
      row: this.row,
      index: this.index,
      id: this.id,
    });
    copy.timeSinceLastMove = this.timeSinceLastMove;
    copy.sprite = this.sprite;
    return copy;
  }

  toJSON(): Record<string, any> {
    return {
      type: "cat",
      id: this.id,
      col: this.col,
      row: this.row,
      index: this.index,
      timeSinceLastMove: this.timeSinceLastMove,
    };
  }
}
